import type { StructuredResult } from "../types";

/**
 * Surfaces per-model Round 1 → Round 2 differences after a deliberative run.
 * Only renders when `deliberative` is on AND the result actually contains
 * two rounds. For single-round runs this component returns `null`.
 */
export function DeliberativeOverview({
  deliberative,
  result,
}: {
  deliberative: boolean;
  result: StructuredResult | null;
}) {
  if (!deliberative || !result) return null;
  const rounds = result.rounds ?? [];
  if (rounds.length < 2) return null;

  // Merge all models seen in either round (a model might have failed in R1
  // and succeeded in R2, or vice-versa).
  const modelIds = new Set<string>();
  for (const r of rounds) {
    for (const resp of r.responses) modelIds.add(resp.model);
    for (const err of r.errors ?? []) modelIds.add(err.model);
  }

  const rows = Array.from(modelIds).map((id) => {
    const r1 = rounds[0].responses.find((x) => x.model === id) ?? null;
    const r2 = rounds[1].responses.find((x) => x.model === id) ?? null;
    const e1 = (rounds[0].errors ?? []).find((x) => x.model === id);
    const e2 = (rounds[1].errors ?? []).find((x) => x.model === id);
    return {
      id,
      r1Resp: r1?.response,
      r2Resp: r2?.response,
      r1Conf: r1?.confidence_score,
      r2Conf: r2?.confidence_score,
      r1Err: e1?.error,
      r2Err: e2?.error,
      changed:
        String(r1?.response ?? "") !== String(r2?.response ?? "") ||
        (r1?.confidence_score ?? null) !== (r2?.confidence_score ?? null),
    };
  });

  if (rows.length === 0) return null;

  return (
    <section
      aria-label="Deliberative round-over-round changes"
      data-testid="deliberative-overview"
      style={{
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-md)",
        background: "var(--bg-panel)",
        padding: "18px 20px",
      }}
    >
      <div
        className="row"
        style={{ justifyContent: "space-between", marginBottom: 12 }}
      >
        <h3 className="section-label" style={{ margin: 0 }}>
          Round-over-round changes
        </h3>
        <span style={{ fontSize: 13, color: "var(--text-dim)" }}>
          Round 1 (pre-deliberation) → Round 2 (post peer review)
        </span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "minmax(160px, 1.4fr) repeat(2, minmax(100px, 1fr)) minmax(80px, 0.8fr)",
            rowGap: 6,
            columnGap: 14,
            alignItems: "baseline",
            fontSize: 14,
            minWidth: 540,
          }}
        >
          <HeaderCell>Model</HeaderCell>
          <HeaderCell>Round 1</HeaderCell>
          <HeaderCell>Round 2</HeaderCell>
          <HeaderCell>Δ</HeaderCell>
          {rows.map((row) => (
            <Row key={row.id} {...row} />
          ))}
        </div>
      </div>
    </section>
  );
}

function HeaderCell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: "var(--text-dim)",
        paddingBottom: 4,
        borderBottom: "1px solid var(--border)",
      }}
    >
      {children}
    </div>
  );
}

function Row(row: {
  id: string;
  r1Resp: unknown;
  r2Resp: unknown;
  r1Conf: number | undefined;
  r2Conf: number | undefined;
  r1Err: string | undefined;
  r2Err: string | undefined;
  changed: boolean;
}) {
  return (
    <>
      <div
        className="mono"
        style={{ fontSize: 13, wordBreak: "break-all", paddingTop: 6 }}
      >
        {row.id}
      </div>
      <Cell resp={row.r1Resp} conf={row.r1Conf} err={row.r1Err} />
      <Cell resp={row.r2Resp} conf={row.r2Conf} err={row.r2Err} highlight={row.changed} />
      <div style={{ paddingTop: 6, color: changeColor(row) }}>{changeLabel(row)}</div>
    </>
  );
}

function Cell({
  resp,
  conf,
  err,
  highlight,
}: {
  resp: unknown;
  conf: number | undefined;
  err?: string;
  highlight?: boolean;
}) {
  if (err !== undefined) {
    return (
      <div
        title={err}
        style={{
          color: "var(--negative)",
          paddingTop: 6,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        failed
      </div>
    );
  }
  const respText = resp === undefined || resp === null ? "—" : String(resp);
  return (
    <div
      style={{
        paddingTop: 6,
        color: highlight ? "var(--accent)" : "var(--text)",
        fontWeight: highlight ? 600 : 400,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
      title={respText}
    >
      {truncate(respText, 22)}
      {typeof conf === "number" && conf >= 0 && (
        <span style={{ color: "var(--text-dim)", marginLeft: 6 }}>
          · {Math.round(conf * 100)}%
        </span>
      )}
    </div>
  );
}

function changeLabel(row: { changed: boolean; r1Conf?: number; r2Conf?: number }): string {
  if (!row.changed) return "no change";
  if (typeof row.r1Conf === "number" && typeof row.r2Conf === "number") {
    const d = Math.round((row.r2Conf - row.r1Conf) * 100);
    if (d === 0) return "revised";
    return `${d > 0 ? "+" : ""}${d}% conf`;
  }
  return "revised";
}

function changeColor(row: { changed: boolean; r1Conf?: number; r2Conf?: number }): string {
  if (!row.changed) return "var(--text-dim)";
  if (typeof row.r1Conf === "number" && typeof row.r2Conf === "number") {
    const d = row.r2Conf - row.r1Conf;
    if (d > 0) return "var(--positive)";
    if (d < 0) return "var(--negative)";
  }
  return "var(--accent)";
}

function truncate(s: string, n: number) {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}
